/**
 * Yardi Credentials tab.
 *
 * Parity target: Angular `yardicreds` template (config.component.html):
 * Platform, Interface Entity, Interface License, Target Env, Data Fetch Range.
 *
 * Parity notes:
 *   - YARDI_INTERFACE_ENTITY and YARDI_INTERFACE_LICENSE are write-only
 *     in Angular: their current values populate the input `placeholder`
 *     but the field itself is empty. Saving an empty value leaves the
 *     existing server value untouched (getFilterValue drops empties).
 *   - YARDI_DATA_FETCH_RANGE_BY_DAYS must be 1–365 inclusive; Angular
 *     blocks submit and toasts if out of range. We mirror that with a
 *     client-side guard + native min/max hints.
 */

import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import {
  buildYardiPayload,
  saveConfig,
} from '../../services/configService'
import type {
  YardiFormValues,
} from '../../types/config'
import { validateYardiDataFetchRangeByDays } from '../../utils/yardiFetchRangeValidation'
import { integerTextInputAttrs, LabeledInput, LabeledTextArea } from './formPrimitives'
import { SectionCard } from '@/components/ui/SectionCard'

import type { YardiTabProps } from '../../types'

const EMPTY: YardiFormValues = {
  YARDI_PLATFORM: '',
  YARDI_INTERFACE_ENTITY: '',
  YARDI_INTERFACE_LICENSE: '',
  YARDI_TARGET_ENV: '',
  YARDI_DATA_FETCH_RANGE_BY_DAYS: '',
}

export function YardiTab({
  initialValues,
  placeholders,
  isLoading,
  onSaved,
}: YardiTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const {
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty, isValid },
  } = useForm<YardiFormValues>({ defaultValues: EMPTY, mode: 'onChange' })

  useEffect(() => {
    if (!initialValues) return
    reset(initialValues)
    void trigger()
  }, [initialValues, reset, trigger])

  const onSubmit = async (values: YardiFormValues) => {
    const rangeCheck = validateYardiDataFetchRangeByDays(values.YARDI_DATA_FETCH_RANGE_BY_DAYS)
    if (rangeCheck !== true) {
      showError(rangeCheck)
      return
    }
    try {
      setSubmitLoading(true)
      await saveConfig(buildYardiPayload(values))
      showSuccess('Config Change Successfully!')
      reset(values)
      onSaved()
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <SectionCard number={1} title="Credentials">
        <div className="space-y-4 max-w-2xl">
          <LabeledInput label="Platform" {...register('YARDI_PLATFORM')} />
          <LabeledInput
            label="Interface Entity"
            placeholder={placeholders.interfaceEntity || 'Leave blank to keep existing value'}
            {...register('YARDI_INTERFACE_ENTITY')}
          />
          <LabeledTextArea
            label="Interface License"
            hint={
              placeholders.interfaceLicense
                ? 'Current value stored — type a new one to overwrite.'
                : 'Leave blank to keep existing value'
            }
            rows={5}
            placeholder={placeholders.interfaceLicense}
            {...register('YARDI_INTERFACE_LICENSE')}
          />
          <LabeledInput label="Target Env" {...register('YARDI_TARGET_ENV')} />
          <LabeledInput
            label="Data Fetch Range By Days"
            requiredMark
            {...integerTextInputAttrs}
            {...register('YARDI_DATA_FETCH_RANGE_BY_DAYS')}
          />
        </div>
      </SectionCard>

      <div>
        <button
          type="submit"
          disabled={!isDirty || submitLoading || !isValid}
          className={primaryEntityActionButtonClass}
        >
          {submitLoading ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </form>
  )
}
